import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import type { WaterPriceListCreateOrUpdate } from '@models/water-price-list';
import { TranslatePipe } from '@ngx-translate/core';
import { fromDateOnly, toDateOnly } from '@utils/date-only';
import type { PriceListForm, WaterPriceListDialogData } from './water-price-list-dialog.model';

/**
 * Name + start date of one price list (CS-232), for both creating and editing. Does no HTTP: it closes
 * with the DTO and the caller sends it, so the same dialog serves the Price lists section and the
 * quick-add beside every price-list dropdown.
 *
 * A future start date is the whole point of the feature — that is how next season's increase is staged —
 * so the picker is deliberately unbounded in both directions. Whether the resulting set of dates leaves
 * the lake priceable is the API's call, not something to second-guess here.
 */
@Component({
  selector: 'app-water-price-list-dialog',
  templateUrl: './water-price-list-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    TranslatePipe,
  ],
})
export class WaterPriceListDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<WaterPriceListDialogComponent, WaterPriceListCreateOrUpdate>);
  private readonly data = inject<WaterPriceListDialogData>(MAT_DIALOG_DATA);

  readonly isEdit = this.data.priceList !== null;

  readonly form = new FormGroup<PriceListForm>({
    name: new FormControl(this.data.priceList?.name ?? '', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)],
    }),
    startDate: new FormControl(fromDateOnly(this.data.priceList?.startDate ?? null), [Validators.required]),
  });

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const startDate = toDateOnly(raw.startDate);
    // Guarded by the required validator above; the null check is what convinces the compiler.
    if (startDate === null) return;

    this.dialogRef.close({ name: raw.name.trim(), startDate });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
